import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { analytics } from '@opendoor/observability/slim';
import { useObservability } from 'helpers/observability';
import { useRouter } from 'next/router';
import {
  documentFor,
  type OfferPortalWizardContext,
  REQUIRED_DOCUMENT_PURPOSES,
  WizardStep,
} from 'state_machines/OfferPortalWizardMachine';
import { z } from 'zod';

import { showsAgentLicenseNumber, showsMlsOfficeId } from './listingDetails';
import { isLoanFinancing } from './offerSubmission';

const OFFER_PORTAL_FORM_ID = 'agent-offer-portal';
const OFFER_PORTAL_VERSION = 'v2';
const offerPortalUtmSourceSchema = z.enum(['agent_tour_rcs']);

const sessionUuid = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const agentIdentifierCompletion = (context: OfferPortalWizardContext): Record<string, boolean> => {
  if (context.agentType === 'mls') {
    return {
      agentMlsId: context.agentMlsId.trim() !== '',
      ...(showsMlsOfficeId(context.listing, context.agentType)
        ? { agentMlsOfficeId: context.agentMlsOfficeId.trim() !== '' }
        : {}),
    };
  }
  if (showsAgentLicenseNumber(context.agentType)) {
    return { agentLicenseNumber: context.agentLicenseNumber.trim() !== '' };
  }
  return {};
};

/**
 * Names every field visible in this form configuration and whether it has a
 * value. Journey's standard formSubmitAttemptEvent requires aggregate visible
 * and completed counts; field names stay local and make the aggregation
 * auditable while conditional fields only enter the map when rendered.
 */
const fieldCompletion = (context: OfferPortalWizardContext): Record<string, boolean> => {
  return {
    price: context.priceCents != null,
    sellerConcession: context.sellerConcessionCents != null,
    buyerAgentCompensation: context.buyerAgentCompensationBps != null,
    earnestDeposit: context.earnestDepositCents != null,
    inspectionPeriod: context.inspectionPeriodDays != null,
    appraisalPeriod: context.appraisalPeriodDays != null,
    financingPeriod: context.financingPeriodDays != null,
    financingType: context.financingType != null,
    ...(isLoanFinancing(context.financingType)
      ? {
          downPayment: context.downPaymentCents != null,
          lenderType: context.lenderType != null,
        }
      : {}),
    closeDate: context.closeDate != null,
    contingentOnSale: context.contingentOnSale != null,
    ...(context.contingentOnSale
      ? { contingentHomeStatus: context.contingentHomeStatus != null }
      : {}),
    buyerFullName: context.buyerFullName.trim() !== '',
    buyerEmail: context.buyerEmail.trim() !== '',
    agentFirstName: context.agentFirstName.trim() !== '',
    agentLastName: context.agentLastName.trim() !== '',
    agentEmail: context.agentEmail.trim() !== '',
    agentPhone: context.agentPhone.trim() !== '',
    agentType: true, // The controlled radio group always has a selected value.
    ...agentIdentifierCompletion(context),
    ...Object.fromEntries(
      REQUIRED_DOCUMENT_PURPOSES.map(
        (purpose) => [purpose, documentFor(context, purpose) != null] as const,
      ),
    ),
  };
};

/**
 * Reporting the form must never be able to break the offer being in it, and
 * `trackJourneyEvent` throws outside production when observability has not
 * initialized yet.
 */
const report = (send: () => void): void => {
  try {
    send();
  } catch {
    // A lost measurement is preferable to a lost offer.
  }
};

const pathVariant = (context: OfferPortalWizardContext): string =>
  [
    context.financingType ?? 'unknown-financing',
    context.contingentOnSale ? 'contingent' : 'not-contingent',
    context.agentType,
  ].join(':');

/**
 * Tracks the v2 portal form lifecycle in both current Journey form events and
 * the legacy resale-offer-portal stream used by the launch reporting. The
 * latter keeps `submit-success` comparable across the Rails and Cosmos flows;
 * `portal_version` is the explicit split between them.
 */
export const useOfferPortalAnalytics = (context: OfferPortalWizardContext, step: WizardStep) => {
  const router = useRouter();
  const { trackJourneyEvent } = useObservability();
  const [formSessionUuid] = useState(sessionUuid);
  const viewedAtRef = useRef<number | null>(null);
  const submitAttemptRef = useRef(0);
  const successTrackedRef = useRef(false);
  const countedViolationFieldsRef = useRef<string[]>([]);
  const validationErrorCountRef = useRef(0);
  const parsedUtmSource = offerPortalUtmSourceSchema.safeParse(router.query.utm_source);
  const utmSource = parsedUtmSource.success ? parsedUtmSource.data : undefined;

  const formContext = useMemo(
    () => ({
      formId: OFFER_PORTAL_FORM_ID,
      formVersion: OFFER_PORTAL_VERSION,
      formSessionUuid,
    }),
    [formSessionUuid],
  );

  const legacyContext = useMemo(
    () => ({
      portal_version: OFFER_PORTAL_VERSION,
      form_id: OFFER_PORTAL_FORM_ID,
      listing_id: context.listing.id,
      listing_state: context.listing.address.state ?? 'unknown',
      ...(utmSource == null ? {} : { utm_source: utmSource }),
    }),
    [context.listing.address.state, context.listing.id, utmSource],
  );

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    viewedAtRef.current = Date.now();
    report(() => trackJourneyEvent('platform', 'formViewEvent', { formContext }));
    // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
    report(() => analytics.trackEvent('resale-offer-portal', 'view', 'form', legacyContext));
  }, [formContext, legacyContext, router.isReady, trackJourneyEvent]);

  useEffect(() => {
    if (successTrackedRef.current || step !== WizardStep.Submitted || context.offerId == null) {
      return;
    }
    successTrackedRef.current = true;
    report(() =>
      // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
      analytics.trackEvent('resale-offer-portal', 'click', 'submit-success', {
        ...legacyContext,
        offer_id: context.offerId,
      }),
    );
  }, [context.offerId, legacyContext, step]);

  // Editing an answer retires its violation, so a field rejected again is a
  // second error rather than the same one still standing.
  useEffect(() => {
    const fields = Object.keys(context.fieldViolations);
    validationErrorCountRef.current += fields.filter(
      (field) => !countedViolationFieldsRef.current.includes(field),
    ).length;
    countedViolationFieldsRef.current = fields;
  }, [context.fieldViolations]);

  const trackSubmitAttempt = useCallback(() => {
    const completion = Object.values(fieldCompletion(context));
    // Read before the SUBMIT event clears the map, so a retry still names the
    // fields intake rejected and the wizard's own validation cannot see.
    const invalidFieldIds = Object.keys(context.fieldViolations);
    submitAttemptRef.current += 1;

    report(() =>
      trackJourneyEvent('platform', 'formSubmitAttemptEvent', {
        formContext,
        totalTimeMs:
          viewedAtRef.current == null ? undefined : Math.max(0, Date.now() - viewedAtRef.current),
        attemptNumber: submitAttemptRef.current,
        pathVariant: pathVariant(context),
        isValid: invalidFieldIds.length === 0,
        invalidFieldIds,
        visibleFieldCount: completion.length,
        completedFieldCount: completion.filter(Boolean).length,
        totalValidationErrorCount: validationErrorCountRef.current,
      }),
    );
    // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
    report(() => analytics.trackEvent('resale-offer-portal', 'click', 'submit', legacyContext));
  }, [context, formContext, legacyContext, trackJourneyEvent]);

  return { trackSubmitAttempt };
};
